import { useCallback, useRef } from "react";
import type { MouseEvent } from "react";
import { Avatar } from "primereact/avatar";
import { OverlayPanel } from "primereact/overlaypanel";

import type { AuditRecord } from "@/modules/audit";
import styles from "./responsavel-tooltip.module.scss";

function getInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";

  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";

  return `${first}${last}`.toUpperCase();
}

interface ResponsavelTooltipProps {
  record: AuditRecord;
}

export function ResponsavelTooltip({ record }: ResponsavelTooltipProps) {
  const displayRecord = record;
  const panel = useRef<OverlayPanel>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelHide = useCallback(() => {
    clearTimeout(closeTimer.current);
  }, []);

  const scheduleHide = useCallback(() => {
    closeTimer.current = setTimeout(() => panel.current?.hide(), 200);
  }, []);

  function handleTriggerMouseEnter(event: MouseEvent<HTMLDivElement>) {
    cancelHide();
    panel.current?.show(event, event.currentTarget);
  }

  async function copyEmail() {
    if (!displayRecord.responsavelEmail) return;

    try {
      await navigator.clipboard.writeText(displayRecord.responsavelEmail);
    } catch {
      return;
    }
  }

  const isSystemUser = displayRecord.responsavel?.trim().toLowerCase() === "system";

  if (!displayRecord.responsavel) return null;

  if (isSystemUser) {
    return (
      <div className={styles.trigger}>
        <span className={styles.responsibleName}>{displayRecord.responsavel}</span>
      </div>
    );
  }

  return (
    <>
      <div
        className={styles.trigger}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={scheduleHide}
      >
        <span className={styles.responsibleName}>{displayRecord.responsavel}</span>
        {displayRecord.responsavelDetalhe ? (
          <span className={styles.responsibleDetail}>{displayRecord.responsavelDetalhe}</span>
        ) : null}
      </div>

      <OverlayPanel ref={panel} className={styles.panel}>
        <div className={styles.card} onMouseEnter={cancelHide} onMouseLeave={scheduleHide}>
          <Avatar
            className={styles.avatar}
            label={getInitials(displayRecord.responsavel)}
            shape="circle"
            aria-label={`Sem foto de ${displayRecord.responsavel}`}
          />

          <div className={styles.userInfo}>
            <p className={styles.userName}>{displayRecord.responsavel}</p>

            <ul className={styles.contacts}>
              {displayRecord.responsavelEmail ? (
                <li className={`${styles.contact} ${styles.contactEmail}`}>
                  <span className={styles.contactMain}>
                    <i className={`pi pi-envelope ${styles.contactIcon}`} aria-hidden />
                    <span className={styles.contactText}>{displayRecord.responsavelEmail}</span>
                  </span>
                  <button
                    type="button"
                    className={styles.copyButton}
                    onClick={copyEmail}
                    aria-label={`Copiar e-mail de ${displayRecord.responsavel}`}
                    data-testid={`responsavel-copy-email-${record.id}`}
                  >
                    <i className="pi pi-copy" aria-hidden />
                  </button>
                </li>
              ) : null}

              {displayRecord.responsavelTelefone ? (
                <li className={styles.contact}>
                  <i className={`pi pi-phone ${styles.contactIcon}`} aria-hidden />
                  <span className={styles.contactText}>{displayRecord.responsavelTelefone}</span>
                </li>
              ) : null}

              {displayRecord.responsavelDetalhe ? (
                <li className={styles.contact}>
                  <i className={`pi pi-users ${styles.contactIcon}`} aria-hidden />
                  <span className={styles.contactText}>{displayRecord.responsavelDetalhe}</span>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      </OverlayPanel>
    </>
  );
}
